import { useParams } from 'react-router-dom'
import { Spinner, TenantImageEnum, useAssetsContext, LootdropProgress, useMediaScreen } from 'summon-ui'
import { useTranslation } from 'summon-ui/intl'
import { Center, Flex, Image, Stack, Title, Text, Badge, Button, TypographyStylesProvider } from 'summon-ui/mantine'
import QuestContainer from '@features/quests/components/QuestContainer'
import useQuestsCategories from '@features/quests/hooks/useQuestCategories'
import useQuests from '@features/quests/hooks/useQuests'
import { Quest } from '@generated/generates'
import { useViewer } from '@hooks'
import Breadcrumbs from '@ui/Breadcrumbs'
import { calculateTotalRewardUnits } from '@utils/questsHelpers'
import { useWeb3Modal } from '@web3modal/wagmi/react'
import InfoCard from '../components/InfoCard'
import useLootdrop from '../hooks/useLootdrop'

const LootdropCategoryDetailsView = ({ category }: { category?: string }) => {
  const t = useTranslation()
  const { categoryId: categoryParamsId } = useParams()
  const { isAuthenticated } = useViewer()
  const { open: openConnectModal } = useWeb3Modal()
  const categoryId = category || categoryParamsId || ''
  const isMaxTablet = useMediaScreen({ type: 'max', breakpoint: 'tablet' })
  const { getTenantImage } = useAssetsContext()
  const questPlaceholder = getTenantImage(TenantImageEnum.QuestPlaceholder)
  const { quests, isLoadingQuests } = useQuests({ categoryId: categoryId })
  const { categoryById } = useQuestsCategories({ categoryId: categoryId })
  const { isLoading: isLootdropLoading, lootdrop } = useLootdrop()

  const isCategoryCompleted = categoryById?.completionPercentage === 100

  if (isLoadingQuests || isLootdropLoading) {
    return (
      <Center h='100%' w='100%'>
        <Spinner />
      </Center>
    )
  }

  const categoryMultiplier = lootdrop?.multiplierPerCategory
    ? `+${lootdrop?.multiplierPerCategory}x ${t('Multiplier')}`
    : undefined
  const totalRewardUnits = calculateTotalRewardUnits(quests)
  const rewardString = totalRewardUnits === 1 ? t('Wearable') : t('Wearables')
  const totalRewardUnitsDisplay = totalRewardUnits ? `${totalRewardUnits} ${rewardString}` : undefined

  const isUpcoming = new Date(categoryById?.startTime) > new Date()

  return (
    <Stack gap='xl' pb='xl'>
      {!category && <Breadcrumbs lastText={categoryById?.name || 'Loading'} />}
      {categoryById && (
        <>
          <Flex direction={isMaxTablet ? 'column' : 'row'} gap='lg' align='center'>
            <Image
              data-testid='lootdrop-category-hero-image'
              alt='quest'
              src={categoryById?.imageUrl || questPlaceholder}
              radius='md'
              w={isMaxTablet ? '100%' : 500}
            />

            <Stack gap='sm' w={isMaxTablet ? '100%' : '50%'}>
              <Title data-testid='category-name-label' fw='bold' lineClamp={2}>
                {categoryById?.name}
              </Title>
              {isCategoryCompleted && (
                <Badge bg='green.9' size='lg'>
                  {t('Completed')}
                </Badge>
              )}
              <TypographyStylesProvider>
                <Text
                  {...(!isMaxTablet ? { lineClamp: 3 } : {})}
                  data-testid='category-description-label'
                  dangerouslySetInnerHTML={{ __html: categoryById?.description || '' }}
                />
              </TypographyStylesProvider>

              {isAuthenticated ? (
                <Stack>
                  <Text>{t('Progress')}</Text>
                  <LootdropProgress value={categoryById?.completionPercentage || 0} />
                  <Flex gap='xs' mt='xs'>
                    <InfoCard
                      info={`${lootdrop?.multiplier?.toString() ?? '1'}x`}
                      description={t('Current Multiplier')}
                      tooltipInfo={{
                        label: t(
                          'Your multiplier increases your chance at receiving rare loot. Level up in the portal and complete weekly LootDrop quests to increase your multiplier'
                        ),
                        w: 200
                      }}
                    />

                    {categoryMultiplier && <InfoCard info={categoryMultiplier} description={t('Reward')} />}
                    {totalRewardUnitsDisplay && <InfoCard info={totalRewardUnitsDisplay} description={t('Reward')} />}
                  </Flex>
                </Stack>
              ) : (
                <Button onClick={() => openConnectModal()} maw={160}>
                  {t('LOGIN / SIGN UP')}
                </Button>
              )}
            </Stack>
          </Flex>
        </>
      )}

      <Stack>
        {quests?.map((quest) => (
          <QuestContainer key={quest.id} quest={quest as Quest} lootdropId={lootdrop?.id} isLocked={isUpcoming} />
        ))}
      </Stack>
    </Stack>
  )
}

export default LootdropCategoryDetailsView
